// Builds add-to-calendar deep links for the web providers (Google, Outlook
// personal, Office365 work, Yahoo) from a single event object. Apple is handled
// separately via the .ics path in calendarInvite.ts, since iOS opens a local
// .ics file in the native Calendar rather than a web URL.
//
// We hand-roll these URLs in ~40 lines rather than pulling in the calendar-link
// npm package. The package is fine for plain web, but this app ships through
// Capacitor, where every added dependency widens the native bundle and the
// chance of a transitive package poking at browser-only globals at import time
// inside the WKWebView. The formats are simple, stable, and easy to unit-test,
// so an internal util is the lower-risk choice and carries no runtime cost.
//
// Reference formats: Google Calendar render TEMPLATE, Outlook/Office365
// deeplink compose, Yahoo calendar v=60. Date encodings differ per provider:
//   - Google + Yahoo: compact basic format YYYYMMDDTHHmmSS.
//   - Outlook + Office365: ISO 8601 extended (YYYY-MM-DDTHH:mm:ss).
//
// PR-E32: these are emitted as FLOATING LOCAL times (no trailing Z, no offset).
// A Pokemon GO / LeekDuck event happens at a given wall-clock time in the user's
// own timezone, so "2:00 PM" must stay "2:00 PM" in whatever zone the device is
// in. Previously the builder ran the value through Date#getUTC* and appended Z,
// which told each provider "this is a UTC instant"; the provider then re-rendered
// it in the device zone and shifted the hour (and sometimes the date) - e.g. on a
// UTC+10 device 14:00 became 00:00 the next day. Floating times carry no zone, so
// no provider re-converts them and the wall-clock numbers survive intact.
//
// The wall-clock numbers are extracted in a timezone-INDEPENDENT way (see
// wallClockParts) so the output is identical regardless of the device or test
// runner timezone.

export interface CalendarLinkEvent {
  title: string;
  description?: string;
  location?: string;
  // YYYY-MM-DD (all-day) or YYYY-MM-DDTHH:mm (timed), same shape the .ics path
  // already consumes from the GameEvent feed.
  start: string;
  end?: string;
}

// A date-only string (no time) is treated as an all-day block starting at
// 18:00, matching the existing .ics generator so a given event lands at the same
// wall-clock time whichever provider the user picks. Timed events with no
// explicit end default to a 3h block.
const ALL_DAY_HOUR = 18;
const DEFAULT_DURATION_HOURS = 3;

// A timezone-free wall-clock instant: just the numbers a human reads off a
// calendar, with no zone attached.
export interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

// Parse an event datetime string into the wall-clock numbers the DEVICE should
// show for it. The feed gives us one of:
//   - "YYYY-MM-DD"                 (date-only; demo + all-day events)
//   - "YYYY-MM-DDTHH:mm[:ss]"      (bare wall-clock: "2 PM" means 2 PM in the
//                                    user's OWN zone, so the literal digits ARE
//                                    the answer)
//   - "YYYY-MM-DDTHH:mm:ss(.sss)Z" (a GENUINE UTC instant. Since the server's
//                                    timeMode fix, /api/events/live preserves
//                                    source strings verbatim: wall-clock events
//                                    arrive bare, and a trailing Z only appears
//                                    on events that truly start everywhere at
//                                    once - the GO Battle League rotations.)
//
// For the bare forms we read the literal digits, so no Date parsing (which would
// interpret them in the device zone) is involved. For the Z form we read the
// LOCAL fields of the instant: a rotation flipping at 20:00 UTC flips at 6 AM
// the next day for an AEST trainer, and saying "8 PM Tuesday" to them put every
// league event on the wrong local day. (Historic note: this branch used to read
// the UTC fields because the pre-timeMode server corrupted wall-clock times
// into fake ...Z strings; that server bug is fixed, so the compat read became
// the bug.)
export function wallClockParts(value: string): WallClock {
  if (value.length <= 10) {
    const [y, m, d] = value.split("-").map((p) => parseInt(p, 10));
    return { year: y, month: m, day: d, hour: ALL_DAY_HOUR, minute: 0, second: 0 };
  }

  if (value.endsWith("Z")) {
    const dt = new Date(value);
    return {
      year: dt.getFullYear(),
      month: dt.getMonth() + 1,
      day: dt.getDate(),
      hour: dt.getHours(),
      minute: dt.getMinutes(),
      second: dt.getSeconds(),
    };
  }

  const m = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!m) {
    // Last-resort fallback: let Date parse it and read UTC fields. Should not
    // happen for the documented input shapes above.
    const dt = new Date(value);
    return {
      year: dt.getUTCFullYear(),
      month: dt.getUTCMonth() + 1,
      day: dt.getUTCDate(),
      hour: dt.getUTCHours(),
      minute: dt.getUTCMinutes(),
      second: dt.getUTCSeconds(),
    };
  }
  return {
    year: parseInt(m[1], 10),
    month: parseInt(m[2], 10),
    day: parseInt(m[3], 10),
    hour: parseInt(m[4], 10),
    minute: parseInt(m[5], 10),
    second: m[6] ? parseInt(m[6], 10) : 0,
  };
}

// PR-?? Countdown fix: convert an event datetime STRING into a device-LOCAL
// epoch (ms) for "starts in / time left" math. This is the countdown twin of
// the add-to-calendar path: both must treat LeekDuck times as floating
// wall-clock numbers ("2:00 PM" = 2:00 PM in the user's OWN zone), never as a
// fixed UTC instant. The naive `new Date(iso).getTime()` was the bug: the live
// server feed serialises the source wall-clock via new Date(local).toISOString()
// -> "...T14:00:00Z", and `new Date(thatZ)` is a real UTC instant, so on a
// UTC+10 device 2 PM rendered as midnight (off by the offset).
//
// wallClockParts() already recovers the intended wall-clock digits from all
// three feed shapes (date-only, bare local, and the corrupted ...Z form). We
// then rebuild them with the LOCAL Date constructor (NOT Date.UTC), which
// interprets the numbers in the device timezone -> exactly "2 PM local". The
// resulting epoch is correct to compare against Date.now() for any user/zone.
export function eventCountdownMs(value: string): number {
  const wc = wallClockParts(value);
  return new Date(
    wc.year,
    wc.month - 1,
    wc.day,
    wc.hour,
    wc.minute,
    wc.second,
  ).getTime();
}

// Build a device-LOCAL Date from an event datetime string (same wall-clock
// recovery as eventLocalMs). Use this for toLocaleDateString/getDate display so
// the day/weekday/time label matches the corrected countdown.
export function eventCountdownDate(value: string): Date {
  return new Date(eventCountdownMs(value));
}

// Add whole hours to a wall-clock instant, rolling over minutes/days/months via
// a UTC Date purely as calendar arithmetic (no zone meaning is attached to the
// result; we immediately read the UTC fields back out).
function addHours(wc: WallClock, hours: number): WallClock {
  const dt = new Date(
    Date.UTC(wc.year, wc.month - 1, wc.day, wc.hour + hours, wc.minute, wc.second),
  );
  return {
    year: dt.getUTCFullYear(),
    month: dt.getUTCMonth() + 1,
    day: dt.getUTCDate(),
    hour: dt.getUTCHours(),
    minute: dt.getUTCMinutes(),
    second: dt.getUTCSeconds(),
  };
}

function resolveRange(event: CalendarLinkEvent): { start: WallClock; end: WallClock } {
  const start = wallClockParts(event.start);
  const end = event.end
    ? wallClockParts(event.end)
    : addHours(start, DEFAULT_DURATION_HOURS);
  return { start, end };
}

// Compact floating basic format: YYYYMMDDTHHmmSS (Google, Yahoo). No trailing Z.
export function toCompactLocal(wc: WallClock): string {
  return (
    wc.year.toString() +
    pad(wc.month) +
    pad(wc.day) +
    "T" +
    pad(wc.hour) +
    pad(wc.minute) +
    pad(wc.second)
  );
}

// ISO 8601 extended floating: YYYY-MM-DDTHH:mm:ss (Outlook, Office365). No Z.
export function toIsoLocal(wc: WallClock): string {
  return (
    wc.year.toString() +
    "-" +
    pad(wc.month) +
    "-" +
    pad(wc.day) +
    "T" +
    pad(wc.hour) +
    ":" +
    pad(wc.minute) +
    ":" +
    pad(wc.second)
  );
}

export interface CalendarLinks {
  google: string;
  outlook: string;
  office365: string;
  yahoo: string;
}

export function buildCalendarLinks(event: CalendarLinkEvent): CalendarLinks {
  const { start, end } = resolveRange(event);
  const title = event.title ?? "";
  const description = event.description ?? "";
  const location = event.location ?? "";

  const compactStart = toCompactLocal(start);
  const compactEnd = toCompactLocal(end);
  const isoStart = toIsoLocal(start);
  const isoEnd = toIsoLocal(end);

  const google = new URL("https://calendar.google.com/calendar/render");
  google.searchParams.set("action", "TEMPLATE");
  google.searchParams.set("text", title);
  google.searchParams.set("dates", `${compactStart}/${compactEnd}`);
  if (description) google.searchParams.set("details", description);
  if (location) google.searchParams.set("location", location);

  const outlookBase = "https://outlook.live.com/calendar/0/deeplink/compose";
  const office365Base = "https://outlook.office.com/calendar/0/deeplink/compose";
  const composeParams = (base: string): string => {
    const u = new URL(base);
    u.searchParams.set("path", "/calendar/action/compose");
    u.searchParams.set("rru", "addevent");
    u.searchParams.set("subject", title);
    u.searchParams.set("startdt", isoStart);
    u.searchParams.set("enddt", isoEnd);
    if (description) u.searchParams.set("body", description);
    if (location) u.searchParams.set("location", location);
    return u.toString();
  };

  const yahoo = new URL("https://calendar.yahoo.com/");
  yahoo.searchParams.set("v", "60");
  yahoo.searchParams.set("title", title);
  yahoo.searchParams.set("st", compactStart);
  yahoo.searchParams.set("et", compactEnd);
  if (description) yahoo.searchParams.set("desc", description);
  if (location) yahoo.searchParams.set("in_loc", location);

  return {
    google: google.toString(),
    outlook: composeParams(outlookBase),
    office365: composeParams(office365Base),
    yahoo: yahoo.toString(),
  };
}
